import type { MainToWorker, WorkerToMain } from './protocol';

export type RfCaptureDirection = 'tx' | 'rx';
export type RfCaptureSource = 'worker' | 'broadcast' | 'online' | 'virtual';

export interface RfCaptureEvent {
  readonly atMs: number;
  readonly phase: string;
  readonly direction: RfCaptureDirection;
  readonly source: RfCaptureSource;
  readonly byte: number;
  readonly peerId?: string;
}

export interface RfCaptureSummaryEntry {
  readonly phase: string;
  readonly direction: RfCaptureDirection;
  readonly source: RfCaptureSource;
  readonly count: number;
  readonly hex: string;
}

export interface RfCaptureSnapshot {
  readonly startedAt: number;
  readonly phase: string;
  readonly events: readonly RfCaptureEvent[];
  readonly summary: readonly RfCaptureSummaryEntry[];
}

export interface RfCaptureSession {
  readonly enabled: true;
  readonly events: readonly RfCaptureEvent[];
  markPhase(phase: string): void;
  record(event: Omit<RfCaptureEvent, 'atMs' | 'phase'> & { readonly phase?: string }): void;
  recordWorkerMessage(message: WorkerToMain): void;
  recordMainMessage(message: MainToWorker, source: RfCaptureSource): void;
  clear(): void;
  snapshot(): RfCaptureSnapshot;
}

declare global {
  interface Window {
    __cybikoRfCapture?: RfCaptureSession;
  }
}

export function initRfCapture(search = window.location.search): RfCaptureSession | undefined {
  if (typeof window === 'undefined') return undefined;
  if (new URLSearchParams(search).get('rfCapture') !== '1') return undefined;

  const session = createRfCaptureSession();
  window.__cybikoRfCapture = session;
  return session;
}

export function getRfCapture(): RfCaptureSession | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.__cybikoRfCapture;
}

export function createRfCaptureSession(): RfCaptureSession {
  const startedAt = Date.now();
  let phase = 'init';
  const events: RfCaptureEvent[] = [];

  const session: RfCaptureSession = {
    enabled: true,
    get events() {
      return events;
    },
    markPhase(nextPhase) {
      phase = sanitizePhase(nextPhase);
    },
    record(event) {
      if (!Number.isInteger(event.byte) || event.byte < 0 || event.byte > 255) return;
      events.push({
        atMs: Date.now() - startedAt,
        phase: event.phase ? sanitizePhase(event.phase) : phase,
        direction: event.direction,
        source: event.source,
        byte: event.byte,
        peerId: event.peerId,
      });
    },
    recordWorkerMessage(message) {
      if (message.type === 'rf-tx') {
        session.record({ direction: 'tx', source: 'worker', byte: message.byte });
      }
    },
    recordMainMessage(message, source) {
      if (message.type === 'rf-rx') {
        session.record({
          direction: 'rx',
          source,
          byte: message.byte,
          peerId: message.peerId,
        });
      }
    },
    clear() {
      events.length = 0;
    },
    snapshot() {
      return {
        startedAt,
        phase,
        events: [...events],
        summary: summarize(events),
      };
    },
  };

  return session;
}

function summarize(events: readonly RfCaptureEvent[]): RfCaptureSummaryEntry[] {
  const groups = new Map<string, RfCaptureEvent[]>();
  for (const event of events) {
    const key = `${event.phase}\u0000${event.direction}\u0000${event.source}`;
    const group = groups.get(key);
    if (group) group.push(event);
    else groups.set(key, [event]);
  }

  return [...groups.entries()].map(([key, group]) => {
    const [phase, direction, source] = key.split('\u0000') as [
      string,
      RfCaptureDirection,
      RfCaptureSource,
    ];
    return {
      phase,
      direction,
      source,
      count: group.length,
      hex: group.map((event) => byteHex(event.byte)).join(' '),
    };
  });
}

function byteHex(byte: number): string {
  return byte.toString(16).padStart(2, '0');
}

function sanitizePhase(phase: string): string {
  const trimmed = phase.trim();
  return trimmed || 'unnamed';
}
